import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from './supabase';

interface AuthError {
  message: string;
}

interface AuthContextType {
  user: User | null;
  signIn: (email: string, password: string, metadata?: object) => Promise<void>;
  signInWithSocial: (provider: 'apple' | 'facebook' | 'google') => Promise<void>;
  signUp: (email: string, password: string, metadata?: object) => Promise<void>;
  signOut: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        setUser(session?.user ?? null);
        
        // Always refresh session to ensure we have valid token
        await supabase.auth.refreshSession();
      } catch (err) {
        console.warn('Failed to get initial session:', err);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      setError(null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message);
      throw err;
    }
  };

  const signInWithSocial = async (provider: 'apple' | 'facebook' | 'google') => {
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
        }
      });
      if (error) throw error;
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message);
      throw err;
    }
  };

  const signUp = async (email: string, password: string, metadata?: object) => {
    setError(null);
    try {
      // Prepare user metadata
      const userMetadata = {
        ...metadata,
        role: metadata && 'role' in metadata ? metadata.role : 'customer',
        status: metadata && 'role' in metadata && metadata.role === 'insurer' ? 'pending' : 'active'
      };

      // Sign up with email verification
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userMetadata,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      
      if (data.user) {
        // Handle insurer signup
        if (userMetadata.role === 'insurer') {
          const { error: insurerError } = await supabase.rpc(
            'create_insurer_profile',
            {
              insurer_id: data.user.id,
              company_name: userMetadata.company_name,
              status: 'pending'
            }
          );
          
          if (insurerError) {
            // If insurer creation fails, we should clean up the auth user
            await supabase.auth.signOut();
            throw new Error(`Failed to create insurer profile: ${insurerError.message}`);
          }
        }

        // Show confirmation message to user
        throw new Error(
          'Please check your email for a confirmation link. ' +
          'You must verify your email before signing in.'
        );
      }
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message);
      throw err;
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      // First clear the user state
      setUser(null);

      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn('Sign out from Supabase failed:', error.message);
      }

      // Clear all auth-related storage and cookies
      localStorage.removeItem('sb-loxmurvvjbsibulluaim-auth-token');
      sessionStorage.removeItem('sb-loxmurvvjbsibulluaim-auth-token');
      document.cookie = 'sb-access-token=; Max-Age=0; path=/; domain=' + window.location.hostname;
      document.cookie = 'sb-refresh-token=; Max-Age=0; path=/; domain=' + window.location.hostname;

    } catch (err) {
      const authError = err as AuthError;
      console.error('Sign out error:', authError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAuth = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      setUser(session?.user ?? null);
    } catch (err) {
      console.warn('Failed to refresh auth:', err);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, signIn, signInWithSocial, signUp, signOut, refreshAuth, isLoading, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}